<template>
    <section aria-labelledby="customer-communications-title" class="review-section">
        <div class="section-heading align-start">
            <div>
                <h2 id="customer-communications-title" class="subtitle-1 font-weight-bold">
                    Comunicazioni al cliente
                </h2>
                <div class="caption grey--text text--darken-1">
                    Conferma prima la presa in carico; invia l’8D quando il rapporto è completato.
                </div>
            </div>
            <v-chip small outlined color="primary">
                <v-icon small left>mdi-reply-outline</v-icon>
                Risposta alla mail
            </v-chip>
        </div>

        <v-alert v-if="result" dense text :type="result.sent ? 'success' : 'info'" class="mt-4 mb-0">
            <div class="font-weight-medium">{{ resultTitle }}</div>
            <div>{{ result.detail }}</div>
            <div v-if="result.attachment_filename" class="caption mt-1">
                Allegato: {{ result.attachment_filename }}
            </div>
            <div v-for="warning in result.warnings || []" :key="warning" class="caption mt-1">
                {{ warning }}
            </div>
        </v-alert>

        <v-row dense class="mt-3">
            <v-col cols="12" md="6">
                <v-card outlined class="communication-card fill-height" :class="{ 'communication-card--disabled': !ncNumber }">
                    <v-card-text>
                        <div class="d-flex align-start">
                            <v-icon color="primary" class="mr-3">mdi-email-check-outline</v-icon>
                            <div>
                                <div class="subtitle-2 font-weight-bold">Conferma di ricezione</div>
                                <div class="caption grey--text text--darken-1 mt-1">
                                    Comunica al mittente che la segnalazione è stata presa in carico.
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text color="primary" @click="openResponse('acknowledgement')">
                            Prepara conferma
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card outlined class="communication-card fill-height">
                    <v-card-text>
                        <div class="d-flex align-start">
                            <v-icon color="primary" class="mr-3">mdi-file-send-outline</v-icon>
                            <div>
                                <div class="subtitle-2 font-weight-bold">Risposta conclusiva 8D</div>
                                <div class="caption grey--text text--darken-1 mt-1">
                                    {{ ncNumber
                                        ? `Allega il PDF 8D della NC ${ncNumber} e invialo nello stesso thread.`
                                        : "Crea prima la NC: l’invio conclusivo sarà disponibile dopo." }}
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text color="primary" :disabled="!ncNumber" @click="openResponse('send_8d')">
                            Prepara invio 8D
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="dialog" max-width="720" persistent>
            <v-card>
                <v-card-title class="dialog-title">
                    <div>
                        <div class="overline primary--text">Risposta a {{ recipient }}</div>
                        <div class="text-h6">{{ dialogTitle }}</div>
                    </div>
                    <v-spacer />
                    <v-btn icon aria-label="Chiudi risposta" :disabled="sending" @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-alert v-if="localPreview" dense text type="info">
                        Modalità locale: la risposta verrà validata senza inviare email reali.
                    </v-alert>
                    <v-text-field :value="recipient" label="Destinatario dalla mail originale" outlined dense disabled />
                    <v-text-field v-model="response.subject" label="Oggetto" outlined dense maxlength="180" counter="180" />
                    <v-textarea
                        v-model="response.body"
                        label="Testo della risposta"
                        outlined
                        auto-grow
                        rows="7"
                        maxlength="5000"
                        counter="5000"
                    />
                    <v-file-input
                        v-if="response.kind === 'send_8d'"
                        v-model="response.attachment"
                        accept="application/pdf,.pdf"
                        label="Rapporto 8D in PDF"
                        prepend-icon="mdi-file-pdf-box"
                        outlined
                        dense
                        show-size
                    />
                    <v-alert
                        v-if="response.kind === 'send_8d' && evidenceAttachmentIds.length"
                        dense
                        text
                        type="warning"
                    >
                        {{ evidenceAttachmentIds.length }} allegati saranno accodati e marcati
                        <strong>PEZZO NON CONFORME</strong> nel PDF inviato.
                    </v-alert>
                    <v-alert v-if="validationError" dense text type="warning" class="mb-0">
                        {{ validationError }}
                    </v-alert>
                    <v-checkbox
                        v-model="recipientConfirmed"
                        class="mt-3"
                        hide-details
                        color="primary"
                        :label="`Ho verificato destinatario e contenuto: ${recipient}`"
                    />
                </v-card-text>
                <v-card-actions class="px-6 pb-5">
                    <v-spacer />
                    <v-btn text :disabled="sending" @click="dialog = false">Annulla</v-btn>
                    <v-btn
                        color="primary"
                        :loading="sending"
                        :disabled="Boolean(validationError) || !recipientConfirmed"
                        @click="submit"
                    >
                        <v-icon left>{{ response.kind === 'send_8d' ? 'mdi-file-send-outline' : 'mdi-send-outline' }}</v-icon>
                        {{ submitLabel }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </section>
</template>

<script>
import { createCustomerResponse, customerResponseValidation } from "./mailSegnalazioniDraft.mjs";

export default {
    name: "MailSegnalazioneReply",
    props: {
        message: { type: Object, required: true },
        localPreview: { type: Boolean, default: true },
        sending: { type: Boolean, default: false },
        result: { type: Object, default: null },
        evidenceAttachmentIds: { type: Array, default: () => [] },
        ncNumber: { type: [String, Number], default: "" },
    },
    data: () => ({
        dialog: false,
        response: createCustomerResponse(),
        recipientConfirmed: false,
    }),
    computed: {
        recipient() {
            return this.message.sender?.email || "Destinatario non disponibile";
        },
        validationError() {
            return customerResponseValidation(this.response);
        },
        dialogTitle() {
            return this.response.kind === "send_8d" ? "Invio del rapporto 8D" : "Conferma di ricezione";
        },
        submitLabel() {
            if (this.localPreview) return "Valida anteprima";
            return this.response.kind === "send_8d" ? "Invia 8D" : "Invia conferma";
        },
        resultTitle() {
            if (this.result.sent) return "Risposta inviata al cliente";
            return this.result.kind === "send_8d" ? "Anteprima invio 8D pronta" : "Anteprima conferma pronta";
        },
    },
    watch: {
        result(value) {
            if (value) this.dialog = false;
        },
    },
    methods: {
        openResponse(kind) {
            if (kind === "send_8d" && !this.ncNumber) return;
            this.response = createCustomerResponse(this.message, kind);
            this.recipientConfirmed = false;
            this.dialog = true;
        },
        submit() {
            if (this.validationError) return;
            this.$emit("send-response", {
                ...this.response,
                evidenceAttachmentIds:
                    this.response.kind === "send_8d" ? [...this.evidenceAttachmentIds] : [],
            });
        },
    },
};
</script>

<style scoped>
.review-section { padding: 20px 0; }
.section-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.communication-card { display: flex; flex-direction: column; justify-content: space-between; }
.communication-card--disabled { opacity: 0.72; }
.dialog-title { border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
@media (max-width: 599px) {
    .section-heading { align-items: flex-start; flex-direction: column; gap: 8px; }
}
</style>
